/**
 * InlineAudioPlayer (PJR-AUDIO-UPLOAD-001) — compact Play/Pause + seek bar + time for a
 * secure attachment. The recording is fetched with the bearer token on first play (never
 * autoplays, no public URL) and held as an object URL for the component lifetime.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';

const fmt = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r < 10 ? '0' : ''}${r}`;
};

export const InlineAudioPlayer = ({ src, authHeaders, mime, testId = 'inline-audio' }) => {
  const audioRef = useRef(null);
  const [objUrl, setObjUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => () => { if (objUrl) URL.revokeObjectURL(objUrl); }, [objUrl]);

  const ensureLoaded = async () => {
    if (objUrl) return objUrl;
    setLoading(true);
    try {
      const r = await fetch(src, { headers: authHeaders || {} });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(mime ? blob.slice(0, blob.size, mime) : blob);
      setObjUrl(url);
      return url;
    } catch (e) {
      setError('Recording unavailable');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (e) => {
    e.stopPropagation();
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); return; }
    const url = await ensureLoaded();
    if (!url) return;
    if (el.src !== url) { el.src = url; }
    try { await el.play(); } catch { setError('Playback failed'); }
  };

  const seek = (e) => {
    e.stopPropagation();
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * duration;
  };

  const pct = duration ? Math.min(100, (time / duration) * 100) : 0;

  return (
    <div className="flex items-center gap-2 w-full min-w-0" data-testid={testId} data-playing={playing ? 'true' : 'false'}>
      <audio
        ref={audioRef}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onError={() => { setError('Playback failed'); setPlaying(false); }}
      />
      <button
        type="button"
        onClick={toggle}
        disabled={loading || !!error}
        title={playing ? 'Pause' : 'Play'}
        aria-label={playing ? 'Pause recording' : 'Play recording'}
        className="h-7 w-7 rounded-full grid place-items-center text-white bg-gradient-to-br from-[#2f5bff] to-[#6a4dff] shadow-sm disabled:opacity-50 transition-transform active:scale-95 shrink-0"
        data-testid={`${testId}-toggle`}
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
      </button>
      {error ? (
        <span className="text-[11px] text-rose-600" data-testid={`${testId}-error`}>{error}</span>
      ) : (
        <>
          <div role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} onClick={seek}
            className="h-1.5 flex-1 rounded-full bg-slate-200 cursor-pointer overflow-hidden" data-testid={`${testId}-seek`}>
            <div className="h-full rounded-full bg-gradient-to-r from-[#2f5bff] to-[#6a4dff] transition-[width] duration-150" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[11px] tabular-nums text-slate-500 shrink-0" data-testid={`${testId}-time`}>
            {fmt(time)}{duration ? ` / ${fmt(duration)}` : ''}
          </span>
        </>
      )}
    </div>
  );
};

export const isAudioAttachment = (a) => a?.kind === 'audio' || /^audio\//.test(a?.content_type || '');

export default InlineAudioPlayer;
